import React, { ChangeEvent, useState } from 'react';
import classes from './UploadRentRoll.module.css';
import { useFullScreenLoader } from '~/hooks/useFullScreenLoader';
import { withFullScreenLoader } from '~/hocs/withFullScreenLoader';
import { useParams } from 'react-router-dom';
import UploadRentRollCsv from './UploadRentRollCsv';
import ColumnMatch from '../RentRoll/ColumnMatch';
import { uniqueId } from 'lodash';
import { TenantInfo } from '../RentRoll/TenantRentRollTable';
import { bulkAddTenants } from '~/api/resident';
import { toast } from 'react-toastify';
import { Colors } from '~/enums/Colors';

const LargeCsvUpload = () => {
  const [parsedCsv, setparsedCsv] = useState<{
    rows: any[];
    maxColumns: number;
  }>();
  const [selectedStartingRow, setselectedStartingRow] = useState<any>();
  const { buildingId } = useParams<{ buildingId: string }>();
  const [currentStep, setCurrentStep] = useState<
    | 'UPLOAD_RENT_ROLL'
    | 'RENTROLL_COLUMN_SELECTION_TABLE'
    | ''
  >('UPLOAD_RENT_ROLL');
  const [fieldMapping, setFieldMapping] = useState<any>({});
  const reset = () => setCurrentStep('');

  const { loader } = useFullScreenLoader();

  const onUploadSuccess = ({
    rows,
    maxColumns
  }: {
    rows: any[];
    maxColumns: number;
  }) => {
    const rowsWithId = rows?.map((row) => {
      row.id = uniqueId();
      return row;
    });

    setparsedCsv({
      rows: rowsWithId,
      maxColumns
    });

    setCurrentStep('RENTROLL_COLUMN_SELECTION_TABLE');
  };

  const onStartingRowSelected = (row: any) => {
    setselectedStartingRow(row);
    const element = document.getElementById(`column-match-radio-${row.id}`);

    if (element?.attributes) {
      // @ts-ignore
      element.checked = true;
    }
  };

  const getTargetedRows = (rows: any[], selectedRowId: string) => {
    const index = rows.findIndex((row) => row.id == selectedRowId);

    const targetRows = rows.slice(index);

    return targetRows;
  };

  const withFirstAndLastName = (tenant: TenantInfo) => {
    if (tenant.fullName && !tenant.firstName && !tenant.lastName) {
      const [firstName, ...lastName] = tenant.fullName.split(' ');
      tenant.firstName = firstName;
      tenant.lastName = lastName.join(' ');
    }
    return tenant;
  };

  const checkIfEmailColumnIsSelected = () => {
    const selectedFields = Object.values(fieldMapping);

    if (!selectedFields.includes('email')) {
      toast.error('Email column not selected from column match table');
      throw Error('email not selected from column match table');
    }
  };

  const checkIfBuildingNameColumnIsSelected = () => {
    const selectedFields = Object.values(fieldMapping);

    if (!selectedFields.includes('buildingName')) {
      toast.error('Building name column not selected from column match table');
      throw Error('building name not selected from column match table');
    }
  };

  const prepareTenantInfo = ({
    rows,
    buildingId
  }: {
    rows: any[];
    buildingId: string;
  }) => {
    const findalData: TenantInfo[] = [];

    const fields: string[] = Object.keys(fieldMapping);

    checkIfEmailColumnIsSelected();

    checkIfBuildingNameColumnIsSelected();

    rows.forEach((row) => {
      let newRow: TenantInfo = {} as TenantInfo;

      const details = { ...row, ...newRow };

      fields.forEach((field: string) => {
        const key = fieldMapping[field] as keyof TenantInfo;
        newRow[key] = row[field];
      });

      newRow = withFirstAndLastName(newRow);
      newRow.details = details;

      if (newRow.email) {
        findalData.push(newRow);
      }
    });

    return findalData;
  };

  function groupTenantsByBuilding(tenants: TenantInfo[]) {
    return tenants.reduce((acc: { [key: string]: TenantInfo[] }, tenant) => {
      const buildingName = tenant.buildingName;
      if (!acc[buildingName]) {
        acc[buildingName] = [];
      }
      acc[buildingName].push({
        ...tenant,
        email: tenant.email ? tenant.email.toLowerCase() : '',
      });
      return acc;
    }, {});
  }

  const displayTenantsImportSuccessAlert = (totalExistingLeads: number, totalLeadsCreated: number) => {
    alert(
      `Tenants successfully imported.
      \n Existing Leads - ${totalExistingLeads}
      \n Leads created - ${totalLeadsCreated}`
    );
  };
  
  const uploadRentRoll = async () => {
    try {
      if (!selectedStartingRow?.id) {
        return toast.error('Starting row not selected');
      }

      const targetRows = getTargetedRows(
        parsedCsv?.rows ?? [],
        selectedStartingRow.id
      );

      const tenants = prepareTenantInfo({
        rows: targetRows,
        buildingId: buildingId
      });

      loader.show();

      const groupedTenants = groupTenantsByBuilding(tenants);

      const response = await Promise.all(Object.values(groupedTenants).map(async (currentBuildingTenants) => {
        const { buildingName } = currentBuildingTenants[0];
        const response = await bulkAddTenants({ buildingName, tenants: currentBuildingTenants });
        return response.data.result;
      }));

      const totalExistingLeads = response.reduce((acc, curr) => acc + curr.intercomExistingLeadsLength, 0);
      const totalLeadsCreated = response.reduce((acc, curr) => acc + curr.intercomLeadsCreatedLength, 0);
      
      toast.success(response[0].data?.message);
      displayTenantsImportSuccessAlert(totalExistingLeads, totalLeadsCreated);
      setCurrentStep('');
    } catch (error) {
      console.error('CSV upload error', error);
      toast.error('The CSV file upload was partially or fully unsuccessful. Please ensure that the file format and data are correct, then try again later.')
    } finally {
      loader.hide();
    }
  };

  const columnNameSelected = (e: ChangeEvent<any>) => {
    setFieldMapping({
      ...fieldMapping,
      [e.target.id]: e.target.value
    });
  };

  return (
    <div className={classes.pageWrapper}>
      {['', 'UPLOAD_RENT_ROLL', 'UPLOAD_MOVEOUT_LIST'].includes(
        currentStep
      ) && (
        <div
          className={`flex-row align-start justify-between ${classes.headerWrapper}`}
        >
          <div>
            <div
              className='margin-bottom-16'
              style={{ fontSize: '32px', color: Colors.cobuDarkBlue }}
            >
              Large CSV Upload
            </div>
            <p>
            From this page, you will upload a CSV file which contains many buildings active rent roll. Then you will map the columns and then process the file for multiple buildings.
            </p>
          </div>
        </div>
      )}
      <UploadRentRollCsv
        onUpload={onUploadSuccess}
      />
      {currentStep == 'RENTROLL_COLUMN_SELECTION_TABLE' && parsedCsv && (
        <ColumnMatch
          rows={parsedCsv.rows}
          fieldMapping={fieldMapping}
          maxColumns={parsedCsv.maxColumns}
          onRowSelection={onStartingRowSelected}
          selectedRow={selectedStartingRow}
          buildingId={buildingId}
          onUpload={uploadRentRoll}
          onColumnNameSelected={columnNameSelected}
          onCancel={reset}
          uploadActionTitle={'Upload Residents'}
        />
      )}
    </div>
  );
};

export default withFullScreenLoader(LargeCsvUpload);
